import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function SiteAuditPage() {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!domain.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/audit/start', { domain, limit: 100 });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  useEffect(() => { if (params.get('domain')) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Site Audit</h1>
        <p className="page-sub">130+ SEO checks, health score, issues, Core Web Vitals — like SEMrush Site Audit · 5 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter domain e.g. yoursite.com" value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !domain.trim()}>{loading ? 'Auditing...' : 'Audit Site →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, marginBottom: 16 }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280', marginBottom: 12 }}>Health Score</div>
              <div style={{ width: 90, height: 90, borderRadius: '50%', border: `4px solid ${data.healthScore > 80 ? '#00C853' : data.healthScore > 60 ? '#FFAB00' : '#FF3D57'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontWeight: 800, fontSize: 28, color: data.healthScore > 80 ? '#00C853' : data.healthScore > 60 ? '#FFAB00' : '#FF3D57' }}>{data.healthScore}</div>
              <div className={`badge ${data.healthScore > 80 ? 'badge-green' : data.healthScore > 60 ? 'badge-yellow' : 'badge-red'}`}>{data.healthStatus}</div>
              <div style={{ marginTop: 12, fontSize: 11, color: '#6B7280' }}>{data.crawledPages} of {data.totalPages} pages crawled</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              <div className="card" style={{ margin: 0, borderLeft: '4px solid #FF3D57' }}><div className="metric-label" style={{ color: '#FF3D57' }}>Errors</div><div className="metric-value" style={{ color: '#FF3D57' }}>{data.issues.errors}</div><div className="metric-sub">critical issues</div></div>
              <div className="card" style={{ margin: 0, borderLeft: '4px solid #FFAB00' }}><div className="metric-label" style={{ color: '#FFAB00' }}>Warnings</div><div className="metric-value" style={{ color: '#FFAB00' }}>{data.issues.warnings}</div><div className="metric-sub">needs attention</div></div>
              <div className="card" style={{ margin: 0, borderLeft: '4px solid #2962FF' }}><div className="metric-label" style={{ color: '#2962FF' }}>Notices</div><div className="metric-value" style={{ color: '#2962FF' }}>{data.issues.notices}</div><div className="metric-sub">improvements</div></div>
              {data.categories.map(c => (
                <div key={c.name} className="card" style={{ margin: 0 }}><div className="metric-label">{c.name}</div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}><div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}><div style={{ width: `${c.score}%`, height: '100%', background: c.score > 80 ? '#00C853' : c.score > 60 ? '#FFAB00' : '#FF3D57', borderRadius: 3 }} /></div><span style={{ fontSize: 12, fontWeight: 700 }}>{c.score}%</span></div><div className="metric-sub" style={{ marginTop: 4 }}>{c.issues} issues</div></div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="tabs">
              <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Top Issues ({data.topIssues.length})</button>
              <button className={`tab-btn ${tab === 'pages' ? 'active' : ''}`} onClick={() => setTab('pages')}>Crawled Pages ({data.pages.length})</button>
              <button className={`tab-btn ${tab === 'vitals' ? 'active' : ''}`} onClick={() => setTab('vitals')}>Core Web Vitals</button>
            </div>

            {tab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.topIssues.map((issue,i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 16px', background: issue.type === 'error' ? '#FFEBEE' : issue.type === 'warning' ? '#FFF8E1' : '#F3F4F6', borderRadius: 6, border: `1px solid ${issue.type === 'error' ? '#FFCDD2' : issue.type === 'warning' ? '#FFECB3' : '#E5E7EB'}` }}>
                    <span className={`badge ${issue.type === 'error' ? 'badge-red' : issue.type === 'warning' ? 'badge-yellow' : 'badge-gray'}`} style={{ height: 20 }}>{issue.type}</span>
                    <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{issue.title} · {issue.count} pages</div><div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{issue.description}</div></div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'pages' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>URL</th><th>Status</th><th>Issues</th><th>Load Time</th><th>Words</th></tr></thead>
                  <tbody>
                    {data.pages.map((p,i) => (
                      <tr key={i}><td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{p.url}</td><td><span className={`badge ${p.status === 200 ? 'badge-green' : p.status === 301 ? 'badge-yellow' : 'badge-red'}`}>{p.status}</span></td><td>{p.issues}</td><td>{p.loadTime}s</td><td>{p.wordCount}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'vitals' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <div className="card" style={{ margin: 0, textAlign: 'center' }}><div className="metric-label">LCP (Largest Contentful Paint)</div><div className="metric-value" style={{ color: parseFloat(data.coreWebVitals.lcp) < 2.5 ? '#00C853' : '#FF3D57' }}>{data.coreWebVitals.lcp}s</div><div className="metric-sub">{parseFloat(data.coreWebVitals.lcp) < 2.5 ? 'Good' : 'Needs improvement'}</div></div>
                <div className="card" style={{ margin: 0, textAlign: 'center' }}><div className="metric-label">FID (First Input Delay)</div><div className="metric-value" style={{ color: data.coreWebVitals.fid < 100 ? '#00C853' : '#FF3D57' }}>{data.coreWebVitals.fid}ms</div><div className="metric-sub">{data.coreWebVitals.fid < 100 ? 'Good' : 'Needs improvement'}</div></div>
                <div className="card" style={{ margin: 0, textAlign: 'center' }}><div className="metric-label">CLS (Cumulative Layout Shift)</div><div className="metric-value" style={{ color: parseFloat(data.coreWebVitals.cls) < 0.1 ? '#00C853' : '#FF3D57' }}>{data.coreWebVitals.cls}</div><div className="metric-sub">{parseFloat(data.coreWebVitals.cls) < 0.1 ? 'Good' : 'Needs improvement'}</div></div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
